/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Bot, Send, ShieldAlert, Loader2,
} from 'lucide-react';
import {
  createTriageSession, sendTriageMessage, finalizeTriageSession,
} from '@/lib/aiTriage';
import { TriageMessage, UrgencyPill } from './triage/TriageMessage';
import { TriageFinalCard } from './triage/TriageFinalCard';


/**
 * AITriageChatbot — Conversational pre-visit triage powered by Claude.
 *
 * Usage:
 *   <AITriageChatbot
 *     open={triageOpen}
 *     onOpenChange={setTriageOpen}
 *     initialContext={{ reason, age, sex }}
 *     appointmentId={appt?.id}   // optional — attaches summary to appointment
 *     onFinalized={(final) => { ... }}
 *   />
 */
const AITriageChatbot = ({
  open,
  onOpenChange,
  initialContext = {},
  appointmentId,
  onFinalized,
  onSessionCreated,
}) => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // [{role: 'assistant'|'user', content, ts}]
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [state, setState] = useState({ urgency_hint: 'low', red_flags: [], running_summary: '', done: false });
  const [finalResult, setFinalResult] = useState(null);
  const [turnsUsed, setTurnsUsed] = useState(0);
  const [maxTurns, setMaxTurns] = useState(8);
  const scrollRef = useRef(null);

  // Start a fresh session when the dialog opens
  useEffect(() => {
    if (!open) return;
    if (sessionId || starting) return;
    (async () => {
      setStarting(true);
      try {
        const res = await createTriageSession({
          reason: initialContext.reason,
          age: initialContext.age,
          sex: initialContext.sex,
          appointment_id: appointmentId,
        });
        setSessionId(res.session_id);
        onSessionCreated?.(res.session_id);
        setMessages([
          { role: 'assistant', content: res.next_question || 'Ciao, iniziamo il triage.', ts: Date.now() },
        ]);
        setState({
          urgency_hint: res.urgency_hint || 'low',
          red_flags: res.red_flags || [],
          running_summary: res.running_summary || '',
          done: !!res.done,
        });
        setTurnsUsed(res.turns_used || 1);
        setMaxTurns(res.max_turns || 8);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Impossibile avviare il triage AI');
      } finally {
        setStarting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, loading, finalResult]);

  const send = async () => {
    const text = input.trim();
    if (!text || !sessionId || loading || state.done || finalResult) return;
    setLoading(true);
    const optimisticUser = { role: 'user', content: text, ts: Date.now() };
    setMessages((m) => [...m, optimisticUser]);
    setInput('');
    try {
      const res = await sendTriageMessage(sessionId, text);
      const assistantMsg = res.next_question
        ? { role: 'assistant', content: res.next_question, ts: Date.now() }
        : null;
      setMessages((m) => (assistantMsg ? [...m, assistantMsg] : m));
      setState({
        urgency_hint: res.urgency_hint || 'low',
        red_flags: res.red_flags || [],
        running_summary: res.running_summary || '',
        done: !!res.done,
      });
      setTurnsUsed(res.turns_used || turnsUsed + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nell\'invio del messaggio');
      // rollback optimistic user message
      setMessages((m) => m.filter((mm) => mm !== optimisticUser));
    } finally {
      setLoading(false);
    }
  };

  const finalize = async () => {
    if (!sessionId || finalizing) return;
    setFinalizing(true);
    try {
      const res = await finalizeTriageSession(sessionId);
      setFinalResult(res.final);
      onFinalized?.(res.final);
      toast.success('Triage completato');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impossibile completare il triage');
    } finally {
      setFinalizing(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="ai-triage-dialog">
        <div className="bg-hero-gradient p-5 border-b border-border">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              Triage AI pre-visita
            </DialogTitle>
            <DialogDescription>
              Il nostro assistente AI ti farà alcune domande brevi per aiutare il medico a prepararsi. Non sostituisce una visita medica.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <UrgencyPill urgency={state.urgency_hint} prefix="Urgenza: " testId="triage-urgency-badge" />
            <span className="text-xs text-muted-foreground" data-testid="triage-turns-counter">
              Turno {turnsUsed}/{maxTurns}
            </span>
            {state.red_flags?.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 text-red-700 px-2.5 py-0.5 text-xs font-semibold" data-testid="triage-redflag-indicator">
                <ShieldAlert className="h-3 w-3" /> {state.red_flags.length} red flag
              </span>
            )}
          </div>
        </div>

        <div ref={scrollRef} className="max-h-[45vh] overflow-y-auto p-5 space-y-3 scrollbar-thin" data-testid="ai-triage-messages">
          {starting && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Avvio del triage AI in corso…
            </div>
          )}
          {messages.map((m, idx) => <TriageMessage key={`${m.ts}-${idx}`} m={m} idx={idx} />)}
          {loading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="triage-loading">
              <Loader2 className="h-3 w-3 animate-spin" /> L'assistente sta pensando…
            </div>
          )}

          {finalResult && <TriageFinalCard finalResult={finalResult} />}
        </div>

        <div className="px-5 py-4 border-t border-border bg-card space-y-3">
          {!finalResult && (
            <>
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder={state.done ? 'Il triage è completo — clicca “Concludi”' : 'Scrivi la tua risposta…'}
                  disabled={loading || starting || state.done}
                  data-testid="ai-triage-input"
                />
                <Button
                  onClick={send}
                  disabled={loading || starting || state.done || !input.trim()}
                  data-testid="ai-triage-send-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-muted-foreground">
                  L'AI non sostituisce il medico. In caso di emergenza chiama il 112.
                </span>
                <Button
                  variant={state.done ? 'default' : 'ghost'}
                  size="sm"
                  onClick={finalize}
                  disabled={finalizing || !sessionId || (turnsUsed < 2 && !state.done)}
                  data-testid="ai-triage-finalize-btn"
                >
                  {finalizing ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Elaborazione</> : 'Concludi triage'}
                </Button>
              </div>
            </>
          )}
          {finalResult && (
            <Button onClick={() => onOpenChange(false)} className="w-full" data-testid="ai-triage-close-btn">
              Chiudi e continua
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AITriageChatbot;
